import { useEffect, useMemo, useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import type {
  BuiltinSopCatalogEntry,
  RemoteDesktopLoginConfig,
} from '@erp/rpc';
import {
  REMOTE_DESKTOP_PORTAL_URLS,
  inferRemoteDesktopPortalTarget,
} from '@erp/shared-types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { useCredentialsList } from 'src/api/rpc/hooks/credentials/useCredentials';
import { useOTPTargetsList } from 'src/api/rpc/hooks/otp/useOTPTargets';
import {
  useBuiltinSopSetup,
  useInstallBuiltinSop,
  useSops,
} from 'src/api/rpc/hooks/agent/useOperatorSops';
import { CredentialFormDialog } from 'src/pages/authenticated/settings/organization/components/cards/credentials/CredentialFormDialog';
import { OTPFormDialog } from 'src/pages/authenticated/settings/organization/components/cards/otp/OTPFormDialog';

const NONE = '__none__';
const DEFAULT_WAIT_SECONDS = 10;

/**
 * Org-specific setup for a `remote_desktop_login` built-in (today: the AVD login).
 *
 * Everything collected here is a REFERENCE — a credential set id, an OTP target id, a tile name.
 * No secret is typed into this form; the two "New…" buttons hand off to the existing org-settings
 * dialogs, which own secret entry and store into the vault.
 */
export function BuiltinSopSetupDialog({
  open,
  onOpenChange,
  organizationId,
  entry,
  onInstalled,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  entry: BuiltinSopCatalogEntry;
  onInstalled?: (sopId: string) => void;
}) {
  const gt = useGT();
  const { data: prefill, isLoading: prefillLoading } = useBuiltinSopSetup(
    organizationId,
    open ? entry.key : undefined,
  );

  const [portalUrl, setPortalUrl] = useState(
    REMOTE_DESKTOP_PORTAL_URLS.full_desktop,
  );
  const [desktopName, setDesktopName] = useState('');
  const [loginCredentialId, setLoginCredentialId] = useState<string | null>(
    null,
  );
  const [vmCredentialSetId, setVmCredentialSetId] = useState<string | null>(
    null,
  );
  const [otpTargetId, setOtpTargetId] = useState<string | null>(null);
  const [authStateTargetId, setAuthStateTargetId] = useState('');
  const [waitSeconds, setWaitSeconds] = useState(DEFAULT_WAIT_SECONDS);
  const [fallbackForSopIds, setFallbackForSopIds] = useState<string[]>([]);
  const [credentialDialogOpen, setCredentialDialogOpen] = useState(false);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);

  // Seed from the server prefill once it lands (and re-seed on reopen), the same
  // open-keyed pattern SopLoginConfigDialog / SopParametersDialog use. The prefill is
  // best-effort, so every field keeps a sensible fallback.
  useEffect(() => {
    if (!open || !prefill) return;
    const config = prefill.remoteDesktopLogin;
    setPortalUrl(config?.portalUrl ?? REMOTE_DESKTOP_PORTAL_URLS.full_desktop);
    setDesktopName(config?.desktopName ?? '');
    setLoginCredentialId(prefill.loginCredentialId);
    setVmCredentialSetId(config?.vmCredentialSetId ?? null);
    setOtpTargetId(config?.otpTargetId ?? null);
    setAuthStateTargetId(config?.authStateTargetId ?? '');
    setWaitSeconds(config?.desktopLoadWaitSeconds ?? DEFAULT_WAIT_SECONDS);
    setFallbackForSopIds([]);
  }, [open, prefill]);

  const { credentials } = useCredentialsList({
    organizationId,
    type: 'password',
  });
  const { configs: otpTargets } = useOTPTargetsList();

  // Same constraint as the login-config dialog's fallback picker: no run-time param binding UI
  // here, so only parameterless procedures can be wired, and never the built-in itself.
  const { sops } = useSops({ organizationId });
  const fallbackCandidates = useMemo(
    () =>
      sops.filter(
        (s) =>
          s.name !== entry.sopName && !s.parameters.some((p) => p.required),
      ),
    [sops, entry.sopName],
  );

  const portalTarget = inferRemoteDesktopPortalTarget(portalUrl);
  const trimmedDesktopName = desktopName.trim();
  const install = useInstallBuiltinSop({
    onSuccess: (response) => {
      onOpenChange(false);
      onInstalled?.(response.sop.id);
    },
  });

  function handleSubmit() {
    const remoteDesktopLogin: RemoteDesktopLoginConfig = {
      portalUrl,
      desktopName: trimmedDesktopName,
      vmCredentialSetId,
      otpTargetId,
      authStateTargetId: authStateTargetId.trim() || null,
      desktopLoadWaitSeconds: waitSeconds,
    };
    install.mutate({
      organizationId,
      builtinKey: entry.key,
      remoteDesktopLogin,
      loginCredentialId,
      fallbackForSopIds,
      // Never force. An upgrade already rewrites the steps because the template version differs;
      // a plain reconfigure must not, or every Save would mint a new SOP version for a settings
      // change that never touched a step.
      force: false,
    });
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{entry.title}</DialogTitle>
            <DialogDescription>{entry.description}</DialogDescription>
          </DialogHeader>

          {prefill && prefill.source !== 'defaults' && (
            <p className="rounded-md bg-muted p-2 text-xs text-muted-foreground">
              {prefill.source === 'agent_definition' ? (
                <T context="Notice that built-in setup was prefilled from an existing remote-desktop agent">
                  Prefilled from your existing remote-desktop agent “
                  <Var>{prefill.sourceLabel ?? ''}</Var>”. Keeping the same
                  settings lets this procedure reuse its cached sign-in instead
                  of repeating MFA.
                </T>
              ) : (
                <T context="Notice that built-in setup was prefilled from the current installation">
                  Showing the settings this procedure is currently installed
                  with.
                </T>
              )}
            </p>
          )}

          <div className="space-y-3">
            <div className="space-y-1">
              <Label>
                <T context="Label for AVD portal target select in built-in setup">
                  Portal target
                </T>
              </Label>
              <Select
                value={portalTarget}
                onValueChange={(value) =>
                  setPortalUrl(
                    value === 'apps'
                      ? REMOTE_DESKTOP_PORTAL_URLS.apps
                      : REMOTE_DESKTOP_PORTAL_URLS.full_desktop,
                  )
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full_desktop">
                    <T context="Portal option: full desktop">Full desktop</T>
                  </SelectItem>
                  <SelectItem value="apps">
                    <T context="Portal option: apps only">Apps</T>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="builtin-desktop-name">
                {portalTarget === 'apps' ? (
                  <T context="Label for app name in built-in setup">App name</T>
                ) : (
                  <T context="Label for desktop name in built-in setup">
                    Desktop name
                  </T>
                )}
              </Label>
              <Input
                id="builtin-desktop-name"
                value={desktopName}
                onChange={(e) => setDesktopName(e.target.value)}
                placeholder={
                  portalTarget === 'apps'
                    ? gt('SAP', { context: 'Example app tile name' })
                    : gt('AVD-FullDesktop', {
                        context: 'Example desktop tile name',
                      })
                }
              />
              <p className="text-xs text-muted-foreground">
                <T context="Hint for the desktop tile name field">
                  The tile to open on the portal's device list — exactly as it
                  appears there.
                </T>
              </p>
            </div>

            <CredentialField
              id="builtin-portal-credential"
              label={
                <T context="Label for the Microsoft portal account credential">
                  Microsoft portal account
                </T>
              }
              hint={
                <T context="Hint for the Microsoft portal account credential">
                  The email + password this procedure signs in with. Without it
                  the sign-in steps have nothing to fill.
                </T>
              }
              value={loginCredentialId}
              onChange={setLoginCredentialId}
              credentials={credentials}
              onCreate={() => setCredentialDialogOpen(true)}
            />

            <CredentialField
              id="builtin-vm-credential"
              label={
                <T context="Label for the in-session VM password credential">
                  In-session VM password
                </T>
              }
              hint={
                <T context="Hint for the VM password credential">
                  A different secret from the portal account — the password the
                  remote desktop itself prompts for.
                </T>
              }
              value={vmCredentialSetId}
              onChange={setVmCredentialSetId}
              credentials={credentials}
              onCreate={() => setCredentialDialogOpen(true)}
            />

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label htmlFor="builtin-otp-target">
                  <T context="Label for the MFA OTP target select">
                    MFA code source
                  </T>
                </Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setOtpDialogOpen(true)}
                >
                  <T context="Button to create a new OTP target">New…</T>
                </Button>
              </div>
              <Select
                value={otpTargetId ?? NONE}
                onValueChange={(value) =>
                  setOtpTargetId(value === NONE ? null : value)
                }
              >
                <SelectTrigger id="builtin-otp-target">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>
                    <T context="Option: no MFA source configured">
                      None (skip MFA)
                    </T>
                  </SelectItem>
                  {otpTargets.map((config) => (
                    <SelectItem key={config.targetId} value={config.targetId}>
                      {config.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="builtin-auth-state">
                <T context="Label for the auth-state key field">
                  Auth-state key (optional)
                </T>
              </Label>
              <Input
                id="builtin-auth-state"
                value={authStateTargetId}
                onChange={(e) => setAuthStateTargetId(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                <T context="Hint for the auth-state key field">
                  Where the signed-in browser session is cached, so later runs
                  can skip MFA. Leave the prefilled value alone to share the
                  cache with your existing remote-desktop agent.
                </T>
              </p>
            </div>

            <div className="space-y-1">
              <Label htmlFor="builtin-wait-seconds">
                <T context="Label for desktop load wait seconds">
                  Desktop load wait (seconds)
                </T>
              </Label>
              <Input
                id="builtin-wait-seconds"
                type="number"
                min={0}
                max={120}
                value={waitSeconds}
                onChange={(e) =>
                  setWaitSeconds(
                    e.target.value
                      ? Number.parseInt(e.target.value, 10)
                      : DEFAULT_WAIT_SECONDS,
                  )
                }
              />
            </div>

            {fallbackCandidates.length > 0 && (
              <div className="space-y-1 border-t pt-3">
                <Label>
                  <T context="Label for choosing procedures that should use this as their fallback login">
                    Use as the fallback login for
                  </T>
                </Label>
                <div className="max-h-32 space-y-1 overflow-y-auto">
                  {fallbackCandidates.map((candidate) => (
                    <label
                      key={candidate.id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={fallbackForSopIds.includes(candidate.id)}
                        onChange={(e) =>
                          setFallbackForSopIds((current) =>
                            e.target.checked
                              ? [...current, candidate.id]
                              : current.filter((id) => id !== candidate.id),
                          )
                        }
                      />
                      <span>{candidate.title}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  <T context="Hint explaining fallback login wiring">
                    Each selected procedure runs this login first whenever its
                    own cached sign-in has expired.
                  </T>
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              <T context="Cancel button">Cancel</T>
            </Button>
            <Button
              disabled={
                install.isPending || prefillLoading || !trimmedDesktopName
              }
              onClick={handleSubmit}
            >
              {entry.installed ? (
                <T context="Button to save changes to an installed built-in">
                  Save settings
                </T>
              ) : (
                <T context="Button to install a built-in procedure">
                  Install procedure
                </T>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Secret entry belongs to the org-settings dialogs — reuse them rather than growing a
          second place that handles credentials. */}
      <CredentialFormDialog
        open={credentialDialogOpen}
        onClose={() => setCredentialDialogOpen(false)}
      />
      <OTPFormDialog
        open={otpDialogOpen}
        onClose={() => setOtpDialogOpen(false)}
      />
    </>
  );
}

function CredentialField({
  id,
  label,
  hint,
  value,
  onChange,
  credentials,
  onCreate,
}: {
  id: string;
  label: React.ReactNode;
  hint: React.ReactNode;
  value: string | null;
  onChange: (value: string | null) => void;
  credentials: { id: string; name: string; usernameMasked?: string | null }[];
  onCreate: () => void;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <Label htmlFor={id}>{label}</Label>
        <Button variant="ghost" size="sm" onClick={onCreate}>
          <T context="Button to create a new credential">New…</T>
        </Button>
      </div>
      <Select
        value={value ?? NONE}
        onValueChange={(next) => onChange(next === NONE ? null : next)}
      >
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>
            <T context="Option: no credential selected">None</T>
          </SelectItem>
          {credentials.map((credential) => (
            <SelectItem key={credential.id} value={credential.id}>
              {credential.name}
              {credential.usernameMasked
                ? ` (${credential.usernameMasked})`
                : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}
